"use client";

import {
	easeInOut,
	motion,
	useMotionValueEvent,
	useReducedMotion,
	useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import styles from "./StickyCtaBarBlock.module.scss";
import { useScroll } from "framer-motion";
import { ButtonLink } from "@/components/shared/Button/Button";
import { PortableTextRichText } from "@/components/shared/PortableTextRichText/PortableTextRichText";
import { CtaQueryResult } from "@/sanity/queries";
import { RichTextSimple } from "@/sanity/types";

interface StickyCtaBarBlockProps {
	message?: RichTextSimple;
	cta: CtaQueryResult | null;
}

/**
 * This component is a sticky CTA bar that appears when the user scrolls down the page.
 * It will appear/disappear based on the scroll position relative to the bounds of its
 * offsetParent (nearest positioned ancestor element)
 */
export const StickyCtaBarBlock = ({ message, cta }: StickyCtaBarBlockProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const boundsRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLBodyElement | null>(null);
	if (typeof window !== "undefined" && bodyRef.current === null) {
		bodyRef.current = document.body as HTMLBodyElement;
	}

	const { scrollYProgress: scrollYProgressIn } = useScroll({
		target: boundsRef,
		container: bodyRef,
		offset: [
			// Begin appearing when the bounds element's start is 0% from the top of the screen
			"start 0%",
			// Finish appearing when the bounds element's start is -50% from the top of the screen
			"start -50%",
		],
	});

	const { scrollYProgress: scrollYProgressOut } = useScroll({
		target: boundsRef,
		container: bodyRef,
		offset: [
			// Begin disappearing when the bounds element's bottom end is 20% from the bottom of the screen
			"end 80%",
			// Finish disappearing when the bounds element's bottom end is 50% from the bottom of the screen
			"end 50%",
		],
	});

	const scrollYProgressInEased = useTransform(scrollYProgressIn, [0, 1], [0, 1], {
		ease: easeInOut,
	});

	const scrollYProgressOutEased = useTransform(scrollYProgressOut, [0, 1], [0, 1], {
		ease: easeInOut,
	});

	const scrollYProgressCombined = useTransform(
		() => scrollYProgressInEased.get() + scrollYProgressOutEased.get(),
	);
	const y = useTransform(scrollYProgressCombined, [0, 1, 2], ["100%", "0%", "100%"]);
	return (
		<>
			<div
				className={styles["sticky-cta-bar-bounds"]}
				ref={boundsRef}
			/>

			<motion.aside
				className={styles["sticky-cta-bar"]}
				ref={ref}
				style={{
					y,
				}}
			>
				<div className={styles["sticky-cta-bar__outer"]}>
					<div className={styles["sticky-cta-bar__inner"]}>
						<div className={styles["sticky-cta-bar__message"]}>
							{message && <PortableTextRichText value={message} />}
						</div>

						{cta?.link && (
							<ButtonLink
								className={styles["sticky-cta-bar__button"]}
								showArrow
								href={cta.link}
								label={cta.label}
								target={cta.openInNewTab ? "_blank" : undefined}
							/>
						)}
					</div>
				</div>
			</motion.aside>
		</>
	);
};
